import {
  Avatar,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import React from "react";
import "./Header.css";
import Grid from "@mui/material/Grid";
import { SignOutButton } from "../../common/commonStyles";
import Badge from "@mui/material/Badge";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { useNavigate } from "react-router-dom";
import MessageIcon from "@mui/icons-material/Message";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { logout, uploadOrganisationLogo } from "../../services/authservice";
import { useDispatch } from "react-redux";
import { setUser } from "../../slice/userDetailsSlice";

// ---- shared palette (matches the rest of the app) -----------------------
const palette = {
  teal: "#4B49AC",
  tealDark: "#37357D",
  tealSoft: "#EEEDF9",
  ink: "#1F2937",
  slate: "#6B7280",
  inactiveIcon: "#475569",
  line: "#E6E9ED",
  coral: "#FF6B57",
};

const iconButtonSx = {
  width: 38,
  height: 38,
  borderRadius: "10px",
  color: palette.inactiveIcon,
  transition: "background-color 120ms ease, color 120ms ease",
  "&:hover": { backgroundColor: palette.tealSoft, color: palette.teal },
};

const menuPaperSx = {
  borderRadius: "14px",
  border: `1px solid ${palette.line}`,
  boxShadow: "0 16px 40px -12px rgba(15, 23, 42, 0.25)",
  mt: "8px",
};

const notificationItemSx = {
  borderRadius: "10px",
  padding: "8px",
  mx: "4px",
  my: "2px",
  "&:hover": { backgroundColor: palette.tealSoft },
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ALLOWED_LOGO_TYPES = ["image/png", "image/jpeg", "image/svg+xml", "image/webp"];
const MAX_LOGO_SIZE = 2 * 1024 * 1024; // 2MB

const Header = ({ userDetail }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [notifAnchorEl, setNotifAnchorEl] = React.useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = React.useState(null);
  const [mobileAnchorEl, setMobileAnchorEl] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);

  const fileInputRef = React.useRef(null);

  const notifOpen = Boolean(notifAnchorEl);
  const profileOpen = Boolean(profileAnchorEl);
  const mobileOpen = Boolean(mobileAnchorEl);

  const handleNotifOpen = (event) => setNotifAnchorEl(event.currentTarget);
  const handleNotifClose = () => setNotifAnchorEl(null);

  const handleProfileOpen = (event) => setProfileAnchorEl(event.currentTarget);
  const handleProfileClose = () => setProfileAnchorEl(null);

  const handleMobileOpen = (event) => setMobileAnchorEl(event.currentTarget);
  const handleMobileClose = () => setMobileAnchorEl(null);

  const handleSignOut = () => {
    logout();
    navigate("/login");
  };

  const orgLogoUrl = `${API_BASE_URL}${userDetail?.organisation_logo }`;

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      alert("Please upload a PNG, JPG, SVG, or WEBP file.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_LOGO_SIZE) {
      alert("File too large. Max size is 2MB.");
      e.target.value = "";
      return;
    }

    try {
      setUploading(true);
      const result = await uploadOrganisationLogo(file);

      dispatch(setUser({ ...userDetail, organisation_logo: result.logo }));
    } catch (error) {
      console.error("Logo upload failed:", error);
      alert("Failed to upload logo. Please try again.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const initials = (userDetail?.name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

  const notifications = [
    { title: "New user registration", time: "2 days ago" },
    { title: "New user registration", time: "1 day ago" },
  ];

  return (
    <Grid container className="header" alignItems="center">
      <Grid size={4}>
        <Box sx={{ position: "relative", display: "inline-block" }}>
          <img
            src={orgLogoUrl}
            alt="Organisation Logo"
            style={{
              height: 45,
              maxHeight: 45,
              cursor: "pointer",
              opacity: uploading ? 0.5 : 1,
              transition: "opacity 150ms ease",
            }}
            onClick={handleLogoClick}
            // onError={(e) => {
            //   e.currentTarget.onerror = null;
            //   e.currentTarget.src = `${API_BASE_URL}`;
            // }}
          />
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            ref={fileInputRef}
            onChange={handleLogoFileChange}
            style={{ display: "none" }}
          />
        </Box>
      </Grid>

      {isMobile ? (
        <Grid size={8} display="flex" justifyContent="end">
          <IconButton
            edge="end"
            aria-label="user menu"
            onClick={handleMobileOpen}
            size="large"
            sx={iconButtonSx}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            anchorEl={mobileAnchorEl}
            open={mobileOpen}
            onClose={handleMobileClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ elevation: 4, sx: { ...menuPaperSx, width: 220 } }}
          >
            <MenuItem disableRipple sx={{ cursor: "default", "&:hover": { backgroundColor: "transparent" } }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  mr: 1.2,
                  fontSize: "13px",
                  fontWeight: 700,
                  backgroundColor: palette.tealSoft,
                  color: palette.teal,
                }}
              >
                {initials}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: palette.ink }}>
                  {userDetail?.name}
                </Typography>
                <Typography sx={{ fontSize: "11.5px", color: palette.slate, textTransform: "capitalize" }}>
                  {userDetail?.role}
                </Typography>
              </Box>
            </MenuItem>
            <Divider sx={{ my: "6px", borderColor: palette.line }} />
            <MenuItem
              onClick={() => {
                handleMobileClose();
                handleSignOut();
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<LogoutRoundedIcon sx={{ fontSize: "16px" }} />}
                sx={[
                  SignOutButton,
                  {
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    borderColor: palette.line,
                    color: palette.coral,
                    "&:hover": { borderColor: palette.coral, backgroundColor: "#FFEFEC" },
                  },
                ]}
                fullWidth
              >
                Sign Out
              </Button>
            </MenuItem>
          </Menu>
        </Grid>
      ) : (
        <Grid size={8} display="flex" justifyContent="end">
          <Grid container spacing={1.5} display="flex" alignItems="center">
            {/* Notifications */}
            <Grid size="auto">
              <IconButton onClick={handleNotifOpen} sx={iconButtonSx}>
                <Badge
                  variant="dot"
                  invisible={false}
                  sx={{ "& .MuiBadge-dot": { backgroundColor: palette.coral } }}
                >
                  <NotificationsNoneOutlinedIcon className="header_icon" />
                </Badge>
              </IconButton>
              <Menu
                anchorEl={notifAnchorEl}
                open={notifOpen}
                onClose={handleNotifClose}
                disableScrollLock
                keepMounted
                PaperProps={{ elevation: 4, sx: { ...menuPaperSx, width: 320, p: 1 } }}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    px: 1,
                    py: 1,
                    fontWeight: 700,
                    fontFamily: "Nunito, sans-serif",
                    fontSize: 15,
                    color: palette.ink,
                  }}
                >
                  Notifications
                </Typography>
                {notifications.map((n, i) => (
                  <MenuItem key={i} sx={notificationItemSx}>
                    <Box display="flex" alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: palette.tealSoft,
                          mr: 1.2,
                          flexShrink: 0,
                        }}
                      >
                        <MessageIcon sx={{ fontSize: "17px", color: palette.teal }} />
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "Nunito, sans-serif",
                            fontSize: 13,
                            fontWeight: 600,
                            color: palette.ink,
                          }}
                        >
                          {n.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Nunito, sans-serif",
                            fontSize: 12,
                            color: palette.slate,
                          }}
                        >
                          {n.time}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>
            </Grid>

            {/* Profile */}
            <Grid size="auto">
              <Box
                onClick={handleProfileOpen}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "6px 10px 6px 6px",
                  borderRadius: "999px",
                  border: `1px solid ${palette.line}`,
                  cursor: "pointer",
                  transition: "background-color 120ms ease",
                  "&:hover": { backgroundColor: palette.tealSoft },
                }}
              >
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: "12px",
                    fontWeight: 700,
                    backgroundColor: palette.teal,
                    color: "#fff",
                  }}
                >
                  {initials}
                </Avatar>
                <Box sx={{ display: { xs: "none", md: "block" } }}>
                  <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: palette.ink, lineHeight: 1.2 }}>
                    {userDetail?.name}
                  </Typography>
                  <Typography sx={{ fontSize: "10.5px", color: palette.slate, textTransform: "capitalize", lineHeight: 1.2 }}>
                    {userDetail?.role}
                  </Typography>
                </Box>
                <KeyboardArrowDownRoundedIcon sx={{ fontSize: "18px", color: palette.slate }} />
              </Box>
              <Menu
                anchorEl={profileAnchorEl}
                open={profileOpen}
                onClose={handleProfileClose}
                disableScrollLock
                PaperProps={{ elevation: 4, sx: { ...menuPaperSx, width: 200 } }}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem
                  onClick={() => {
                    handleProfileClose();
                    handleSignOut();
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    color: palette.coral,
                    fontWeight: 600,
                    fontSize: "13.5px",
                    borderRadius: "8px",
                    mx: "4px",
                    "&:hover": { backgroundColor: "#FFEFEC" },
                  }}
                >
                  <LogoutRoundedIcon sx={{ fontSize: "17px" }} />
                  Sign Out
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default Header;